import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';

const MotifPlacementOverlay = ({ motif, onPlacementChange, placement, cellSize, colorSource, chartColors, chartSettings }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Get motif dimensions
  const motifData = motif.data || motif.colordata || [];
  const motifRows = motifData.length;
  const motifCols = motifData[0] ? motifData[0].length : 0;
  
  // Get the cell aspect ratio
  const yscale = chartSettings?.yscale || 1;

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - placement.col * cellSize,
      y: e.clientY - placement.row * cellSize * yscale
    });
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMoveGlobal = (e) => {
      if (!isDragging) return;

      const newCol = Math.max(0, Math.round((e.clientX - dragStart.x) / cellSize));
      const newRow = Math.max(0, Math.round((e.clientY - dragStart.y) / (cellSize * yscale)));
      
      onPlacementChange({ row: newRow, col: newCol });
    };

    const handleMouseUpGlobal = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMoveGlobal);
      document.addEventListener('mouseup', handleMouseUpGlobal);
      return () => {
        document.removeEventListener('mousemove', handleMouseMoveGlobal);
        document.removeEventListener('mouseup', handleMouseUpGlobal);
      };
    }
  }, [isDragging, dragStart, cellSize, yscale, onPlacementChange]);

  return (
    <Box
      sx={{
        position: 'absolute',
        left: placement.col * cellSize,
        top: (placement.row+1) * cellSize * yscale,
        width: motifCols * cellSize,
        height: motifRows * cellSize * yscale,
        border: '2px dashed #2196f3',
        backgroundColor: 'rgba(33, 150, 243, 0.1)',
        cursor: isDragging ? 'grabbing' : 'grab',
        pointerEvents: 'auto',
        zIndex: 1000
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Motif Preview */}
      <Box sx={{ 
        position: 'relative', 
        width: '100%', 
        height: '100%',
        pointerEvents: 'none',
        overflow: 'hidden'
      }}>
        {motifData.map((row, dataRowIndex) =>
          row.map((colorIndex, dataColIndex) => {
            // Skip transparent/empty cells (typically 0 for background in some motifs)
            if (colorIndex === null || colorIndex === undefined) return null;
            
            // Get color from selected source
            const motifColors = motif.colors || ['#FFFFFF', '#000000', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF'];
            const colorsToUse = colorSource === 'motif' ? motifColors : (chartColors || motifColors);
            const cellColor = colorsToUse[colorIndex] || '#CCCCCC';
            
            // Skip if color is the same as background (for better visibility)
            const isBackground = colorIndex === 0 && colorsToUse[0] === '#FFFFFF';
            if (isBackground) return null;
            
            return (
              <Box
                key={`${dataRowIndex}-${dataColIndex}`}
                sx={{
                  position: 'absolute',
                  left: dataColIndex * cellSize,
                  top: dataRowIndex * cellSize * yscale,
                  width: cellSize,
                  height: cellSize * yscale,
                  backgroundColor: cellColor,
                  opacity: 0.8,
                  border: cellSize > 6 ? '1px solid rgba(255,255,255,0.3)' : 'none',
                  boxSizing: 'border-box'
                }}
              />
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default MotifPlacementOverlay;
